import React, { useEffect, useMemo, useState } from 'react';
import { ACTIVE_SEASON } from '../config/season';
import { apiService } from '../services/api';
import './SponsorBar.css';

const DEFAULT_DESCRIPTION = 'More details about this partner will be announced soon.';

const parseSeasonNumber = (label = '') => {
  const match = String(label).match(/\d+/);
  return match ? Number(match[0]) : 0;
};

const normalizeInstagramUrl = (value) => {
  if (!value) {
    return null;
  }

  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value;
  }

  return `https://www.instagram.com/${value.replace(/^@/, '')}/`;
};

const mapPartner = (partner) => ({
  id: String(partner.id || partner.sponsor_id || partner.name || Math.random()),
  seasonId: String(partner.season_id || ''),
  name: partner.sponsors?.name || partner.sponsor_name || partner.name || 'Official Partner',
  sponsorType: partner.sponsor_type,
  displayOrder: Number(partner.display_order ?? Number.MAX_SAFE_INTEGER),
  logo: partner.sponsors?.logo || partner.logo_url || partner.logo || '/images/pg_main_logo.webp',
  description:
    typeof partner.description === 'string' && partner.description.trim().length > 0
      ? partner.description.trim()
      : DEFAULT_DESCRIPTION,
  websiteUrl: partner.sponsors?.website || partner.website_url || partner.website || partner.url || null,
  instagramUrl: normalizeInstagramUrl(
    partner.sponsors?.instagram || partner.instagram_url || partner.instagram || partner.instagram_handle || null
  ),
});

const SponsorBar = () => {
  const [seasonTabs, setSeasonTabs] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState('');
  const [partnersBySeasonId, setPartnersBySeasonId] = useState({});
  const [selectedPartnerBySeason, setSelectedPartnerBySeason] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isContentFading, setIsContentFading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadSeasons = async () => {
      setIsLoading(true);
      try {
        const [seasonsResponse, sponsorsResponse] = await Promise.all([
          apiService.getSeasons(),
          apiService.getSponsors(null, true),
        ]);

        if (!isMounted) {
          return;
        }

        const dbSeasons = (seasonsResponse.data || []).map((season) => ({
          id: String(season.id),
          label: season.name || 'Season',
          number: parseSeasonNumber(season.name),
        }))
        .filter((season) => season.number >= 3);

        const fallbackSeasons = [5, 4, 3,].map((seasonNumber) => ({
          id: `fallback-season-${seasonNumber}`,
          label: `Season ${seasonNumber}`,
          number: seasonNumber,
          isFallback: true,
        }));

        const mergedByNumber = new Map();
        [...fallbackSeasons, ...dbSeasons].forEach((season) => {
          if (!season.number) {
            return;
          }
          mergedByNumber.set(season.number, season);
        });

        const sortedTabs = Array.from(mergedByNumber.values()).sort((a, b) => b.number - a.number);
        setSeasonTabs(sortedTabs);

        const groupedPartners = (sponsorsResponse.data || []).reduce((acc, rawPartner) => {
          const mapped = mapPartner(rawPartner);
          if (!mapped.seasonId) {
            return acc;
          }

          if (!acc[mapped.seasonId]) {
            acc[mapped.seasonId] = [];
          }

          acc[mapped.seasonId].push(mapped);
          return acc;
        }, {});

        Object.keys(groupedPartners).forEach((seasonId) => {
          groupedPartners[seasonId].sort((a, b) => a.displayOrder - b.displayOrder);
        });

        setPartnersBySeasonId(groupedPartners);

      
        const selectedBySeason = {};
        Object.keys(groupedPartners).forEach((seasonId) => {
          if (groupedPartners[seasonId]?.length) {
            selectedBySeason[seasonId] = groupedPartners[seasonId][0].id;
          }
        });
        setSelectedPartnerBySeason(selectedBySeason);

        const active = sortedTabs.find((season) => season.number === Number(ACTIVE_SEASON));
        setSelectedSeasonId((active || sortedTabs[0] || {}).id || '');
      } catch (error) {
        console.error('Error loading seasons for partners:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadSeasons();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedSeasonId) {
      return;
    }

    setIsContentFading(true);
    const timer = setTimeout(() => {
      setIsContentFading(false);
    }, 240);

    return () => clearTimeout(timer);
  }, [selectedSeasonId]);

  const selectedSeason = useMemo(
    () => seasonTabs.find((season) => season.id === selectedSeasonId) || null,
    [seasonTabs, selectedSeasonId]
  );

  const seasonPartners = useMemo(() => {
    if (!selectedSeason || selectedSeason.isFallback) {
      return [];
    }
    return partnersBySeasonId[selectedSeason.id] || [];
  }, [selectedSeason, partnersBySeasonId]);

  useEffect(() => {
    if (!selectedSeason || selectedSeason.isFallback || seasonPartners.length === 0) {
      return;
    }

    const selectedForSeason = selectedPartnerBySeason[selectedSeason.id];
    if (!selectedForSeason || !seasonPartners.some((partner) => partner.id === selectedForSeason)) {
      setSelectedPartnerBySeason((prev) => ({
        ...prev,
        [selectedSeason.id]: seasonPartners[0].id,
      }));
    }
  }, [selectedSeason, selectedPartnerBySeason, seasonPartners]);

  const featuredPartner =
    seasonPartners.find((partner) => partner.id === selectedPartnerBySeason[selectedSeasonId]) ||
    seasonPartners[0] ||
    null;

  const hasPartners = seasonPartners.length > 0;

  return (
    <section className="sponsor-showcase" aria-label="Official partners">
      <div className="sponsor-showcase__header">
        <p className="sponsor-showcase__kicker">OFFICIAL PARTNERS</p>
        <p className="sponsor-showcase__subtitle">Showcasing the brands that help power the VPL ecosystem.</p>
      </div>

      <div
        className="partners-season-tabs"
        role="tablist"
        aria-label="Select season"
        style={{ '--tab-count': seasonTabs.length }}
      >
        {seasonTabs.map((season) => (
          <button
            key={season.id}
            className={`partners-season-tab ${
              season.id === selectedSeasonId ? 'partners-season-tab-active' : ''
            }`}
            onClick={() => setSelectedSeasonId(season.id)}
            role="tab"
            aria-selected={season.id === selectedSeasonId}
            disabled={isLoading}
          >
            {season.label}
          </button>
        ))}
      </div>

      {isLoading && <p className="partners-status">Loading partners...</p>}

      {!isLoading && (
        <div className={`partners-content ${isContentFading ? 'partners-content--fading' : ''}`}>
          {featuredPartner && (
            <article className="featured-partner" aria-live="polite">
              <div className="featured-partner__logo-wrap">
                <span className="featured-partner__logo-glow" aria-hidden="true" />
                <img
                  src={featuredPartner.logo}
                  alt={featuredPartner.name}
                  loading="lazy"
                  className="featured-partner__logo"
                />
              </div>

              <div className="featured-partner__details">
                {featuredPartner.sponsorType && (
                  <span className="featured-partner__badge">{featuredPartner.sponsorType}</span>
                )}
                <h3 className="featured-partner__name">{featuredPartner.name}</h3>
                <p className="featured-partner__description">{featuredPartner.description}</p>

                {(featuredPartner.websiteUrl || featuredPartner.instagramUrl) && (
                  <div className="featured-partner__actions">
                    {featuredPartner.websiteUrl && (
                      <a
                        href={featuredPartner.websiteUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="partner-btn partner-btn--primary"
                      >
                        Visit Website
                      </a>
                    )}
                    {featuredPartner.instagramUrl && (
                      <a
                        href={featuredPartner.instagramUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="partner-btn partner-btn--secondary"
                      >
                        Instagram
                      </a>
                    )}
                  </div>
                )}
              </div>
            </article>
          )}

          {!hasPartners && <p className="partners-status">Partners for this season will be announced soon.</p>}

          {hasPartners && (
            <div className="partner-selector" role="tablist" aria-label="Season partners">
              {seasonPartners.map((partner) => (
                <button
                  key={partner.id}
                  className={`partner-tile ${partner.id === featuredPartner?.id ? 'partner-tile--active' : ''}`}
                  onClick={() =>
                    setSelectedPartnerBySeason((prev) => ({
                      ...prev,
                      [selectedSeasonId]: partner.id,
                    }))
                  }
                  role="tab"
                  aria-selected={partner.id === featuredPartner?.id}
                  aria-label={`Show ${partner.name} details`}
                >
                  <span className="partner-tile__logo-wrap">
                    <img src={partner.logo} alt={partner.name} loading="lazy" className="partner-tile__logo" />
                  </span>
                  <span className="partner-tile__name">{partner.name}</span>
                  <span className="partner-tile__role">{partner.sponsorType || ' '}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default SponsorBar;
